import { createRootRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { Sentry } from "@/src/sentry";
import { Providers } from "@/src/providers";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { NativeBoot } from "@/components/native-boot";
import { SwUpdater } from "@/components/sw-updater";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, FileQuestion } from "lucide-react";
import Link from "@/components/ui/link-compat";

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: GlobalError,
  notFoundComponent: NotFound,
});

function RootComponent() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <Providers>
        <AuthProvider>
          <NativeBoot />
          <SwUpdater />
          <Outlet />
        </AuthProvider>
      </Providers>
      <Toaster />
    </ThemeProvider>
  );
}

function GlobalError({ error, reset }: { error: unknown; reset: () => void }) {
  useEffect(() => {
    Sentry.captureException(error);
    console.error("Unhandled error:", error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>An unexpected error occurred. Please try again.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button onClick={reset}>Try Again</Button>
          <Button variant="outline" onClick={() => { window.location.href = "/dashboard"; }}>
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted-foreground/10">
            <FileQuestion className="h-6 w-6 text-muted-foreground" />
          </div>
          <CardTitle>Page not found</CardTitle>
          <CardDescription>
            The page you&apos;re looking for doesn&apos;t exist or has been moved.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button asChild>
            <Link href="/dashboard">Go to Dashboard</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href="/login">Go to Login</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
